import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, RootState } from '@/store';
import { getCouponList, createCoupon, editCoupon } from '@/store/coupon/action';
import { getUserVenues } from '@/store/user-info/action';
import useCustomPaging from '@/hooks/useCustomPaging';
import useSearchFilter from '@/hooks/useSearchFilter';
import useUserVenueSelectFilter from '@/hooks/useUserVenueSelectFilter';
import { dynamicAttr } from '@/utils/fallback';
import { constructTableMetaFromPaging2 } from '@/utils/paging';
import { currencyFormat } from '@/utils'
import {
	renderTableTitle,
	renderTableDiscountType,
	renderTableQuota,
	renderTablePeriod,
	renderTableStatus,
	renderTableEditButton, 
	renderTableVisibility,
	renderTableCreatedAt} from '@/pages/Coupon/Table'
import {
	renderCouponFormHeader,
	renderCouponFormBody,
	renderCouponFormFooter
} from '@/pages/Coupon/Modal'
import { ReactComponent as AddIcon } from '@/assets/icons/ri-add-line.svg'
import BreadcrumbLayout from '@/components/common/CustomLayout/BreadcrumbLayout'
import Table from '@/components/common/Table'
import Button from '@/components/common/Button'
import Modal from '@/components/common/Modal'
import { STATUS_FILTER_COLOR_MAPPING, STATUS } from '@/pages/Coupon/constant'
import UserVenueSelect from "@/components/common/UserVenueSelect";

const renderCouponHeader = (
	searchString: string,
	handleChangeSearchString: any,
	handleSubmitSearch: any,
	showModal: Function) => (
	<BreadcrumbLayout>
		<div className="flex justify-between items-center gap-8 p-0">
			<p className="font-bold text-2xl text-black-primary">Coupon</p>
			<div className="flex items-center gap-4">
				<input
					value={searchString}
					onChange={handleChangeSearchString}
					onKeyDown={handleSubmitSearch}
					name='searchCoupon'
					type='text'
					className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
					placeholder='Search for Coupon'
				/>
				<div className="bg-grey-line-subtle h-6 w-1"/>
				<Button type="submit" variant="primary" className="text-white" onClick={() => showModal()}>
					<AddIcon/>
					<p>Coupon</p>
				</Button>
			</div>
		</div>
	</BreadcrumbLayout>
)

const renderFilterSection = (venueOptions, selectedVenue, handleChangeVenue, selectFilter, filterMeta, couponSummary: {[key: string]: number}) => (
	<div className="flex justify-between items-center p-4 p-6 bg-white border border-grey-line-subtle border-b-0 rounded-t">
		<div className="flex items-center p-0 gap-4">
			<div className="flex items-start">
				{STATUS.map(status => {
					const btnClass = status?.key === filterMeta?.status ? STATUS_FILTER_COLOR_MAPPING?.ACTIVE : STATUS_FILTER_COLOR_MAPPING?.INACTIVE
					return (
						<Button type='button'
										variant='primary'
										key={status?.key}
										className={
											`border
											 border-grey-line-subtle
											 rounded-none 
											 gap-3
											 ${btnClass?.button}`}
										onClick={() => selectFilter({ status: status?.key })}>
							{status?.label}
							<div
								className={`flex py-0 px-2 gap-3 rounded-full ${btnClass?.count}`}>
								{couponSummary[status?.key?.toLowerCase()] || 0}
							</div>
						</Button>)
				})}
			</div>
			<div className="bg-grey-line-subtle h-6 w-1"/>
			<UserVenueSelect
				useIcon={false}
				options={venueOptions}
				value={selectedVenue}
				placeholder='Select venue'
				onChange={handleChangeVenue}
			/>
		</div>
		<div className="flex items-center p-0 gap-2">
			{/* TODO: To dropdown and do actual pagination */}
			10 Item Per Page
		</div>
	</div>
)

// const sortFunction = ({ v1, v2, direction, key }: CouponCustomSortParams) => {
// 	if (direction === 'DESC') {
// 		if (key) {
// 			return v2[key].toString().localeCompare(v1[key].toString())
// 		}
// 		return v2.toString().localeCompare(v1.toString())
// 	}
// 	if (key) {
// 		return v1[key].toString().localeCompare(v2[key].toString())
// 	}
// 	return v1.toString().localeCompare(v2.toString())
// }

const HEADERS: TableHeader[] = [
	{
		label: 'Title',
		key: 'name',
	},
	{
		label: 'Discount Type',
		key: 'discountType',
	},
	{
		label: 'Venue',
		key: 'venue',
	},
	{
		label: 'Min Purchase',
		key: 'minPrice'
	},
	{
		label: 'Usage Quota',
		key: 'remainingQuota'
	},
	{
		label: 'Periode',
		key: 'startAt'
	},
	{
		label: 'Visibility',
		key: 'visibility',
	},
	{
		label: 'Created',
		key: 'createdAt',
	},
	{
		label: 'Status',
		key: 'status',
	},
	{
		label: 'Action',
		key: 'action',
	}
]
const SORT: TableSort = {
	type: 'SERVER',
	handler: () => {}
}

const constructTableData = (data, showModal) => {
	const tableBody = data.map(coupon => {
		const {
			id,
			name,
			code,
			type,
			discount_amount: discountAmount,
			discount_percentage: discountPercentage,
			max_discount_amount: maxDiscount,
			venue,
			min_price: minPrice,
			quota,
			remaining_quota: remainingQuota,
			start_at: startAt,
			end_at: endAt,
			created_at: createdAt,
			status,
			visibility
		} = coupon
		return {
			id,
			value:
				{
					name: {
						couponTitle: name,
						couponCode: code
					},
					discountType: {
						discountType: type,
						discountAmount,
						discountPercentage,
						maxDiscount
					},
					venue: {
						label: venue.name,
						value: venue.id
					},
					minPrice,
					remainingQuota: {
						remaining: remainingQuota,
						max: quota
					},
					startAt: {
						startDate: startAt,
						endDate: endAt,
					},
					visibility,
					createdAt,
					status,
					action: {
						id,
						startDate: startAt,
						endDate: endAt
					}
				}
		}
	})
	const component = {
		name: (v: CouponTableTitleParams) => renderTableTitle(v),
		discountType: (v: CouponTableDiscountParams) => renderTableDiscountType(v),
		venue: (v: { label: string, value: string }) => <p>{v.label}</p>,
		minPrice: (v: number) => (<p>{currencyFormat(v)}</p>),
		remainingQuota: (v: CouponTableQuotaParams) => renderTableQuota(v),
		startAt: (v: CouponTablePeriodParams) => renderTablePeriod(v),
		visibility: (v: string) => renderTableVisibility(v),
		status: (v: string) => renderTableStatus(v),
		createdAt: (v: number) => renderTableCreatedAt(v),
		action: (v: { id: string, startDate?: number, endDate?: number }) => renderTableEditButton(v, showModal),
	}
	return {
		data: tableBody,
		component
	}
}

const Coupon = (): JSX.Element => {
	const dispatch = useAppDispatch()
	const { items: coupons, summary: couponSummary } = useSelector((state: RootState) => state.coupon.state)
	const paging = useSelector((state: RootState) => state.coupon.state.meta)
	const isLoading = useSelector((state: RootState) => state.coupon.loading)
	const [show, setShow] = useState(false)
	const [couponId, setCouponId] = useState('')
	const [isModalLoading, setIsModalLoading] = useState(false)
	const [startDate, setStartDate] = useState(new Date().getTime())
	const [endDate, setEndDate] = useState(new Date().getTime())
	const [searchParams, setSearchParams] = useSearchParams()
	const venueState = useSelector((state: RootState) => state.userInfo.state.venues)
	const [currentPageFilter,,handlePageNumberChange] = useCustomPaging()
	const [
		currentVenueIdFilter,
		selectedVenue,
		venueOptions,
		handleChangeVenue,
	] = useUserVenueSelectFilter()
	const [
		searchString,
		currentSearchFilter,
		handleChangeSearchString,
		handleSubmitSearch
	] = useSearchFilter()

	const methods = useForm()
	const showModal = (v?: string, start?: number, end?: number) => {
		if (v) {
			setCouponId(v)
			setStartDate(start)
			setEndDate(end)
		} else {
			setStartDate(new Date().getTime())
			setEndDate(new Date().getTime())
		}
		setShow(true)
	}
	const closeModal = () => {
		setCouponId('')
		setShow(false)
		setIsModalLoading(false)
	}
	const submitModal = async () => {
		const statusMeta = searchParams.get('status') === 'ALL' ? '' : searchParams.get('status')
		const venueMeta = searchParams.get('venue')
		const searchMeta = searchParams.get('search')
		const couponData = methods.getValues()
		const sortMeta = "-createdAt"
		const data = {
			"name": couponData.couponTitle,
			"type": couponData.discountType?.value,
			"min_price": Number(couponData.minPurchase) || 0,
			"discount_percentage": Number(couponData.discountPercent) || 0,
			"discount_amount": Number(couponData.discountPercent) || 0,
			"max_discount_amount": Number(couponData.maxDiscount) || 0,
			"quota": Number(couponData.usageQuota) || 0,
			"start_at": startDate,
			"end_at": endDate,
			"code": couponData.couponCode,
			"status": couponData.status,
			"venue_id": couponData.venue?.value,
			"visibility": couponData.visibility?.value,
		}
		setIsModalLoading(true)
		if (couponId) {
			await dispatch(editCoupon({data, id: couponId})).then(async (res: {[key: string]: any}) => {
				if (!res.error) {
					closeModal()
					await dispatch(getCouponList({statusMeta, venueMeta, searchMeta, sortMeta}))
				}
			})
			return
		}
		await dispatch(createCoupon(data)).then(async (res: {[key: string]: any}) => {
			if (!res.error) {
				closeModal()
				await dispatch(getCouponList({statusMeta, venueMeta, searchMeta, sortMeta}))
			}
		})
	}

	const couponData = (id: string) => constructTableData(coupons, () => {}).data.find(row => row.value.action.id === id)?.value
	const couponMeta: CouponFilter = {
		status: searchParams.get('status'),
		venue: searchParams.get('venue'),
		search: searchParams.get('search')
	}
	const handleCouponMeta = ({ status, venue, search }: CouponFilter) => {
		if (status) searchParams.set('status', status)
		if (venue || venue === '') searchParams.set('venue', venue)
		if (search || search === '') searchParams.set('search', search)
		setSearchParams(searchParams)
	}

	const showDiscountForm = methods.watch('discountType')?.value === 'PERCENT'

	const setSortingParam = (key, order) => order === 'ASC' ? key : `-${key}`

	const getAllCoupons = () => {
		const params = {
			sizeMeta: 10,
			...dynamicAttr('pageMeta', currentPageFilter),
			...dynamicAttr('venueMeta', currentVenueIdFilter),
			...dynamicAttr('searchMeta', currentSearchFilter),
			...dynamicAttr('statusMeta', searchParams.get('status') === 'ALL' ? '' : searchParams.get('status')),
			...dynamicAttr('sortMeta', setSortingParam(searchParams.get('sortBy') || 'createdAt', searchParams.get('order')) || 'DESC'),
		}
		dispatch(getCouponList(params))
	}

	useEffect(() => {
		(async () => {
			await dispatch(getUserVenues({}))
		})()
	}, [])
	useEffect(() => {
		(async () => {
			getAllCoupons()
		})()
	}, [
		couponMeta.status,
		currentPageFilter,
		currentVenueIdFilter,
		currentSearchFilter,
		searchParams.get('sortBy'),
		searchParams.get('order')
	])
	useEffect(() => {
		if (couponId) {
			const coupon = couponData(couponId)
			const initialCoupon = {
				couponTitle: coupon.name.couponTitle,
				couponCode: coupon.name.couponCode,
				usageQuota: coupon.remainingQuota.max,
				discountType: { label: coupon.discountType.discountType, value: coupon.discountType.discountType },
				discountPercent: coupon.discountType.discountPercentage,
				maxDiscount: coupon.discountType.maxDiscount,
				minPurchase: coupon.minPrice,
				startDate: coupon.startAt.startDate,
				endDate: coupon.startAt.endDate,
				venue: { label: coupon?.venue?.label, value: coupon?.venue?.value },
				status: coupon.status,
				visibility: { label: coupon.visibility, value: coupon.visibility },
			}
			methods.reset({...initialCoupon})
		} else { 
			const initialCoupon = {
				status: 'ACTIVE',
			}
			methods.reset({...initialCoupon}) 
		}
	}, [methods.reset, couponId])
	useEffect(() => {
		if (showDiscountForm) {
			methods.register('discountPercent')
			methods.register('maxDiscount')
		} else {
			methods.unregister('discountPercent')
			methods.unregister('maxDiscount')
		}
	}, [methods.register, methods.unregister, showDiscountForm])
	return (
		<div className='flex flex-col w-full'>
			{renderCouponHeader(searchString, handleChangeSearchString, handleSubmitSearch, showModal)}
			<div className='flex flex-col p-4 w-full'>
				{renderFilterSection(venueOptions, selectedVenue, handleChangeVenue, handleCouponMeta, couponMeta, couponSummary)}
				{!isLoading && <Table headers={HEADERS}
							 	body={constructTableData(coupons, showModal)}
							 	sort={SORT}
								meta={constructTableMetaFromPaging2(paging)}
								pageNumberHandler={handlePageNumberChange}/>}
			</div>
			{ show && <Modal show={show}
							header={renderCouponFormHeader}
							body={renderCouponFormBody({ methods }, couponId, startDate, endDate, venueState, () => submitModal, showDiscountForm, setStartDate, setEndDate)}
							footer={renderCouponFormFooter(() => closeModal, () => submitModal, isModalLoading)}
							onClick={() => closeModal}
							rootId="portal-coupon-form"/> }
		</div>
	)
}

export default Coupon
